/**
 * RevealButton — Button with cursor-tracking circle reveal on hover.
 *
 * A circle expands from the cursor entry point, revealing inverted colors
 * (white bg + blue text for primary, blue bg + white text for secondary).
 *
 * The overlay renders a CLONE of the same @affine/component Button as the
 * base, so the revealed layout is byte-for-byte identical to the unrevealed
 * one — only the colors differ (handled in styles.css via CSS var overrides).
 * Never hand-roll the overlay metrics; cloning the Button is what keeps the
 * two layers aligned across every size/variant.
 *
 * Usage:
 *   <RevealButton label="Sign in" />                       // primary, extraLarge
 *   <RevealButton label="Continue" prefix={<GoogleIcon />} badge="Last used" />
 *   <RevealButton label="Back" variant="secondary" />
 */

import clsx from 'clsx';
import {
  type CSSProperties,
  forwardRef,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useRef,
} from 'react';

import { Button, type ButtonProps } from '../button';
import * as styles from './styles.css';

export interface RevealButtonProps extends Omit<
  ButtonProps,
  'children' | 'withoutHover'
> {
  /** Button text */
  label: ReactNode;
  /** Optional "last used"-style ribbon badge in the bottom-right corner */
  badge?: string;
  /** Wrapper style overrides */
  wrapperStyle?: CSSProperties;
  /** Wrapper className */
  wrapperClassName?: string;
}

export const RevealButton = forwardRef<HTMLDivElement, RevealButtonProps>(
  function RevealButton(
    {
      label,
      badge,
      variant = 'primary',
      size = 'extraLarge',
      className,
      wrapperStyle,
      wrapperClassName,
      disabled,
      loading,
      prefix,
      suffix,
      style: buttonStyle,
      ...buttonProps
    },
    externalRef
  ) {
    const internalRef = useRef<HTMLDivElement>(null);

    const setRef = useCallback(
      (el: HTMLDivElement | null) => {
        (internalRef as { current: HTMLDivElement | null }).current = el;
        if (typeof externalRef === 'function') externalRef(el);
        else if (externalRef)
          (externalRef as { current: HTMLDivElement | null }).current = el;
      },
      [externalRef]
    );

    const trackMouse = useCallback((e: ReactMouseEvent) => {
      const el = internalRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      el.style.setProperty('--rx', `${x}%`);
      el.style.setProperty('--ry', `${y}%`);
    }, []);

    // Base button has no border/radius — the wrapper draws those so the
    // reveal circle is clipped cleanly.
    const btnStyle: CSSProperties = {
      border: 'none',
      borderRadius: 0,
      width: '100%',
      ...buttonStyle,
    };

    const renderButton = (overlay: boolean) => (
      <Button
        className={className}
        variant={variant}
        size={size}
        disabled={disabled}
        // Overlay never shows a spinner — it only renders while !loading.
        loading={overlay ? undefined : loading}
        withoutHover
        block
        prefix={prefix}
        suffix={suffix}
        style={btnStyle}
        {...buttonProps}
        {...(overlay
          ? { tabIndex: -1, 'aria-hidden': true, 'data-testid': undefined }
          : {})}
      >
        {label}
      </Button>
    );

    const node = (
      <div
        ref={setRef}
        className={clsx(styles.wrapper, wrapperClassName)}
        style={wrapperStyle}
        data-variant={variant}
        data-disabled={disabled || loading || undefined}
        onMouseEnter={trackMouse}
        onMouseMove={trackMouse}
      >
        {renderButton(false)}
        {/* Overlay: identical layout, inverted colors, clipped by circle */}
        {!loading && <div className={styles.overlay}>{renderButton(true)}</div>}
      </div>
    );

    if (!badge) return node;

    return (
      <div className={styles.badgeAnchor}>
        {node}
        <span className={styles.badge}>{badge}</span>
      </div>
    );
  }
);
